import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
    DEEP_LINK_TARGETS,
    DeepLinkTargetParam,
} from '@/components/shared/constants/deepLinkTargets';

const TAB_PARAM = 'tab';
const TIMEOUT_MS = 10000;
const AUTO_DISMISS_MS = 10000;

const TARGET_PARAMS = Object.keys(DEEP_LINK_TARGETS) as DeepLinkTargetParam[];

export const useDeepLinkStatus = (isPageReady: boolean) => {
    const [searchParams] = useSearchParams();
    const [timedOutTargetKey, setTimedOutTargetKey] = useState<string | null>(null);
    const [dismissedTargetKey, setDismissedTargetKey] = useState<string | null>(null);

    const targetParam = TARGET_PARAMS.find((param) => searchParams.get(param)) ?? null;
    const targetId = targetParam ? searchParams.get(targetParam) : null;
    const target = targetParam ? DEEP_LINK_TARGETS[targetParam] : null;
    const targetKey = targetParam && targetId ? `${targetParam}:${targetId}` : null;
    const hasTimedOut = targetKey !== null && targetKey === timedOutTargetKey;
    const isDismissed = targetKey !== null && targetKey === dismissedTargetKey;
    const isOnOwningTab = target ? searchParams.get(TAB_PARAM) === target.tab : false;
    const isMissing = target ? !isOnOwningTab || hasTimedOut : false;
    const isPending = Boolean(target) && !isMissing;

    useEffect(() => {
        setTimedOutTargetKey(null);
        if (!targetKey || !isPageReady) return;
        const timer = setTimeout(() => setTimedOutTargetKey(targetKey), TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [targetKey, isPageReady]);

    const dismiss = useCallback(() => {
        if (!targetKey) return;
        setDismissedTargetKey(targetKey);
    }, [targetKey]);

    useEffect(() => {
        if (!isMissing || isDismissed) return;
        const timer = setTimeout(dismiss, AUTO_DISMISS_MS);
        return () => clearTimeout(timer);
    }, [isMissing, isDismissed, dismiss]);

    return {
        pendingLabel: isPending && !isDismissed ? target?.label : undefined,
        missingLabel: isMissing && !isDismissed ? target?.label : undefined,
        dismiss,
    };
};
